import { StyleProvider } from '@ant-design/cssinjs';
import { App as AntApp, ConfigProvider, theme as antdTheme } from 'antd';
import enGB from 'antd/locale/en_GB';
import { createContext, use, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';

import { FONT, NAV, palette, themeConfig, type ThemeMode } from './tokens';

const STORAGE_KEY = 'tlp.admin.theme';
const DARK_QUERY = '(prefers-color-scheme: dark)';

/**
 * What the user *chose*, which is not the same thing as what is painted.
 *
 * `system` is the default, and the reason this type has to exist: someone whose
 * laptop flips to dark at sunset expects the tools on it to flip too, and a
 * two-way toggle can only answer that by making them come back and flip it
 * themselves. Everything downstream — the tokens, the antd config, `Brand`, the
 * maps — keeps taking the resolved `ThemeMode`, so `system` never leaks past
 * this file.
 */
export type ThemePreference = ThemeMode | 'system';

type ThemeContextValue = {
  /** What is actually painted. `system` is already resolved here. */
  mode: ThemeMode;
  /** What the user picked. Render the control from this, never from `mode`. */
  preference: ThemePreference;
  setPreference: (preference: ThemePreference) => void;
  /** Light ↔ dark, for a plain two-way control. Steps out of `system`. */
  toggle: () => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function systemMode(): ThemeMode {
  return window.matchMedia(DARK_QUERY).matches ? 'dark' : 'light';
}

/*
 * Storage can throw outright — Safari in private mode, a locked-down profile —
 * and a colour preference is not worth a blank screen, so both directions
 * swallow. A stored 'light' or 'dark' from before this file grew a third option
 * still reads back as itself, so nobody's existing choice is lost.
 */
function readPreference(): ThemePreference {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === 'light' || stored === 'dark' || stored === 'system') return stored;
  } catch {
    /* No storage. Fall through and follow the OS. */
  }
  return 'system';
}

function writePreference(preference: ThemePreference) {
  try {
    localStorage.setItem(STORAGE_KEY, preference);
  } catch {
    /* The preference holds for this session and is forgotten on the next. */
  }
}

/**
 * Publishes the resolved antd tokens as the CSS variables `styles.css` maps
 * into Tailwind. It sits inside ConfigProvider because `useToken` has to read
 * the config above it — which is why this is a component and not a hook call in
 * ThemeProvider itself.
 *
 * Colours come from the resolved token so Tailwind cannot disagree with what
 * antd actually painted; the ring and the shadows come straight from the
 * palette, because antd has no token for either.
 */
function TailwindTokenBridge({ mode, children }: { mode: ThemeMode; children: ReactNode }) {
  const { token } = antdTheme.useToken();

  useEffect(() => {
    const p = palette(mode);
    const root = document.documentElement;
    const vars: Record<string, string> = {
      '--tlp-brand': token.colorPrimary,
      '--tlp-surface': token.colorBgLayout,
      '--tlp-surface-raised': token.colorBgContainer,
      '--tlp-elevated': token.colorBgElevated,
      '--tlp-ink': token.colorText,
      '--tlp-ink-muted': token.colorTextSecondary,
      '--tlp-ink-subtle': token.colorTextTertiary,
      '--tlp-hairline': token.colorBorderSecondary,
      /* The control edge. `styles.css` publishes it as a Tailwind colour too. */
      '--tlp-hairline-strong': p.borderStrong,
      /* The neutral hover/selected fill — shadcn's `--accent`. */
      '--tlp-muted': token.colorFillTertiary,
      /* One step heavier than muted. The current nav row sits on this. */
      '--tlp-muted-strong': token.colorFillSecondary,
      '--tlp-positive': token.colorSuccess,
      '--tlp-caution': token.colorWarning,
      '--tlp-danger': token.colorError,
      /* The wash and the ink under a destructive row on hover. antd has neither. */
      '--tlp-danger-soft': p.dangerSoft,
      '--tlp-danger-strong': p.dangerStrong,
      '--tlp-font-body': FONT.body,
      '--tlp-font-display': FONT.display,
      '--tlp-font-mono': FONT.mono,
      /* The sidebar's rail, so CSS and the layout cannot drift apart. */
      '--nav-gutter': `${NAV.gutter}px`,
      '--nav-rail': `${NAV.railInset}px`,
      '--tlp-radius': `${token.borderRadius}px`,
      '--tlp-radius-lg': `${token.borderRadiusLG}px`,
      '--tlp-ring': p.ring,
      '--tlp-shadow-sm': p.shadowSm,
      '--tlp-shadow-md': p.shadowMd,
    };
    for (const [name, value] of Object.entries(vars)) root.style.setProperty(name, value);

    /*
     * Two things outside the variable bridge that also have to follow the mode.
     *
     * `data-theme` is the hook for rules that cannot be expressed as a swapped
     * variable, and it is what the boot script in `index.html` sets before
     * React mounts so the first paint is already the right colour.
     *
     * The address-bar colour was pinned to the brand green, which on a phone
     * left a green band above a near-black page in dark mode. It follows the
     * page ground now, so the browser chrome continues the app rather than
     * interrupting it.
     */
    root.dataset.theme = mode;
    document.querySelector('meta[name="theme-color"]')?.setAttribute('content', p.bg);
  }, [token, mode]);

  return children;
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [preference, setPreferenceState] = useState<ThemePreference>(readPreference);
  const [system, setSystem] = useState<ThemeMode>(systemMode);

  /*
   * Kept live rather than read once at boot, because reading once is the exact
   * failure this preference exists to prevent: the machine goes dark at sunset
   * and the one tab that has been open since lunch stays white.
   */
  useEffect(() => {
    const media = window.matchMedia(DARK_QUERY);
    const sync = (event: MediaQueryListEvent) => setSystem(event.matches ? 'dark' : 'light');
    media.addEventListener('change', sync);
    // The OS may have moved between the first read and this subscription.
    setSystem(media.matches ? 'dark' : 'light');
    return () => media.removeEventListener('change', sync);
  }, []);

  const mode: ThemeMode = preference === 'system' ? system : preference;

  const setPreference = useCallback((next: ThemePreference) => {
    setPreferenceState(next);
    writePreference(next);
  }, []);

  useEffect(() => {
    // Lets native widgets (scrollbars, date inputs, autofill) match the theme.
    document.documentElement.style.colorScheme = mode;
  }, [mode]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      preference,
      setPreference,
      toggle: () => setPreference(mode === 'dark' ? 'light' : 'dark'),
    }),
    [mode, preference, setPreference],
  );

  return (
    <ThemeContext value={value}>
      {/* `layer` puts antd's runtime CSS in @layer antd so Tailwind wins ties. */}
      <StyleProvider layer>
        <ConfigProvider theme={themeConfig(mode)} locale={enGB} componentSize="middle">
          <TailwindTokenBridge mode={mode}>
            {/* Supplies the message/modal/notification instances that read theme context. */}
            <AntApp className="h-full">{children}</AntApp>
          </TailwindTokenBridge>
        </ConfigProvider>
      </StyleProvider>
    </ThemeContext>
  );
}

export function useThemeMode(): ThemeContextValue {
  const context = use(ThemeContext);
  if (!context) throw new Error('useThemeMode must be used inside <ThemeProvider>');
  return context;
}
